import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from './DataTable';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';

function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [quickAddName, setQuickAddName] = useState('');
  const [editingVendor, setEditingVendor] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', contactPerson: '', phone: '', email: '', address: '' });
  const { alert, showAlert, hideAlert } = useAlert();

  useEffect(() => {
    fetchVendors();
    document.title = 'Vendors - Saral Asset Manager';
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendors');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickAddName.trim()) return;
    try {
      await axios.post('/api/vendors', {
        name: quickAddName.trim(),
        contactPerson: '',
        phone: '',
        email: '',
        address: ''
      });
      setQuickAddName('');
      showAlert('success', 'Vendor added successfully!');
      fetchVendors();
    } catch (error) {
      if (error.response?.status === 409) {
        showAlert('danger', 'Vendor with this name already exists!');
      } else {
        showAlert('danger', 'Error adding vendor');
      }
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setEditForm({
      name: vendor.name,
      contactPerson: vendor.contactPerson || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || ''
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/vendors/${editingVendor.id}`, {
        ...editForm,
        id: editingVendor.id
      });
      setEditingVendor(null);
      showAlert('success', 'Vendor updated successfully!');
      fetchVendors();
    } catch (error) {
      if (error.response?.status === 409) {
        showAlert('danger', 'Vendor with this name already exists!');
      } else {
        showAlert('danger', 'Error updating vendor');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        await axios.delete(`/api/vendors/${id}`);
        showAlert('success', 'Vendor deleted successfully!');
        fetchVendors();
      } catch (error) {
        showAlert('danger', 'Error deleting vendor');
      }
    }
  };

  const columns = [
    { field: 'name', label: 'Name' },
    { field: 'contactPerson', label: 'Contact Person' },
    { field: 'phone', label: 'Phone' },
    { field: 'email', label: 'Email' },
    { field: 'createdAt', label: 'Created At', render: (value) => new Date(value).toLocaleDateString() }
  ];

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      <div className="mb-3">
        <h1 className="page-title mb-0">Vendors</h1>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0"><i className="fas fa-plus-circle me-2"></i>Quick Add</h6>
        </div>
        <div className="card-body">
          <form onSubmit={handleQuickAdd} className="row g-2">
            <div className="col-md-8">
              <input
                type="text"
                className="form-control"
                placeholder="Enter vendor name (e.g., ABC Computers, etc.)"
                value={quickAddName}
                onChange={(e) => setQuickAddName(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <button type="submit" className="btn btn-success w-100" disabled={!quickAddName.trim()}>
                <i className="fas fa-plus me-1"></i>Add
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <DataTable
            data={vendors}
            columns={columns}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {editingVendor && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Vendor</h5>
                <button type="button" className="btn-close" onClick={() => setEditingVendor(null)}></button>
              </div>
              <form onSubmit={handleUpdate}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Contact Person</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.contactPerson}
                      onChange={(e) => setEditForm({...editForm, contactPerson: e.target.value})}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={editForm.address}
                      onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setEditingVendor(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Update</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Vendors;